// Lightweight DOM helper. No virtual DOM; views are functions that
// return a host element and an `unmount` cleanup. This is enough for
// a six-section desktop shell and keeps the diff between rebuilds
// trivially small.

export type View = {
  el: HTMLElement;
  update?(ctx: { param?: string | null }): void;
  destroy?(): void;
};

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, children: Array<Node | string> = []): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = String(v);
    else if (k === 'style' && v && typeof v === 'object') Object.assign((el as HTMLElement).style, v as Record<string, string>);
    else if (k === 'dataset' && v && typeof v === 'object') Object.assign((el as HTMLElement).dataset, v as Record<string, string>);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (v === true) el.setAttribute(k, '');
    else if (v === false || v == null) { /* skip */ }
    else el.setAttribute(k, String(v));
  }
  for (const c of children) el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  return el;
}

export function clear(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function toast(text: string, kind: 'info' | 'error' | 'ok' = 'info', timeoutMs = 3000): void {
  let host = document.getElementById('toast-host');
  if (!host) {
    host = h('div', { id: 'toast-host', class: 'toast-host', role: 'status', 'aria-live': 'polite' });
    document.body.appendChild(host);
  }
  const t = h('div', { class: `toast toast-${kind}`, role: kind === 'error' ? 'alert' : 'status' }, [text]);
  host.appendChild(t);
  setTimeout(() => { t.classList.add('leaving'); setTimeout(() => t.remove(), 250); }, timeoutMs);
}

export function confirmModal(opts: { title: string; body: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = h('div', { class: 'modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title });
    const card = h('div', { class: 'modal-card' });
    card.append(
      h('h2', { class: 'modal-title' }, [opts.title]),
      h('p', { class: 'modal-body' }, [opts.body]),
    );
    const actions = h('div', { class: 'modal-actions' });
    const cancel = h('button', { class: 'btn ghost', type: 'button' }, [opts.cancelLabel ?? 'Cancelar']);
    const confirm = h('button', { class: opts.danger ? 'btn danger' : 'btn primary', type: 'button' }, [opts.confirmLabel ?? 'Confirmar']);
    cancel.addEventListener('click', () => { overlay.remove(); resolve(false); });
    confirm.addEventListener('click', () => { overlay.remove(); resolve(true); });
    actions.append(cancel, confirm);
    card.append(actions);
    overlay.append(card);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) { overlay.remove(); resolve(false); } });
    document.body.appendChild(overlay);
    modalKeys(overlay, cancel, () => { overlay.remove(); resolve(false); });
    cancel.focus();
  });
}

function modalKeys(overlay: HTMLElement, initial: HTMLElement, cancel: () => void): void {
  const previous = document.activeElement as HTMLElement | null;
  overlay.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); previous?.focus(); }
    if (e.key !== 'Tab') return;
    const controls = Array.from(overlay.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled])'));
    const first = controls[0]; const last = controls.at(-1);
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  });
  const observer = new MutationObserver(() => { if (!overlay.isConnected) { observer.disconnect(); if (previous?.isConnected) previous.focus(); } });
  observer.observe(document.body, { childList: true, subtree: true });
  initial.focus();
}

/** Editable dialog: keeps submitted values visible when the server rejects a save. */
export function formModal(opts: {
  title: string; body?: string; submitLabel?: string; danger?: boolean;
  fields: Array<{ name: string; label: string; value?: string; multiline?: boolean }>;
  onSave: (values: Record<string, string>) => Promise<void>;
}): Promise<boolean> {
  return new Promise(resolve => {
    const overlay = h('div', { class: 'modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title });
    const card = h('form', { class: 'modal-card form-stack' });
    card.append(h('h2', { class: 'modal-title' }, [opts.title]));
    if (opts.body) card.append(h('p', { class: 'modal-body' }, [opts.body]));
    const inputs = new Map<string, HTMLInputElement | HTMLTextAreaElement>();
    for (const field of opts.fields) {
      const input = field.multiline ? h('textarea', { class: 'textarea', required: true, maxlength: 2000 }) : h('input', { class: 'input', required: true, maxlength: 2000 });
      input.value = field.value ?? ''; input.name = field.name;
      inputs.set(field.name, input);
      card.append(h('label', { class: 'field' }, [h('span', {}, [field.label]), input]));
    }
    const error = h('p', { class: 'auth-error', role: 'alert' });
    const cancel = h('button', { class: 'btn ghost', type: 'button' }, ['Cancelar']);
    const submit = h('button', { class: opts.danger ? 'btn danger' : 'btn primary', type: 'submit' }, [opts.submitLabel ?? 'Guardar']);
    let pending = false;
    const close = () => { if (!pending) { overlay.remove(); resolve(false); } };
    cancel.addEventListener('click', close);
    card.append(error, h('div', { class: 'modal-actions' }, [cancel, submit]));
    overlay.append(card); document.body.append(overlay);
    modalKeys(overlay, inputs.values().next().value ?? cancel, close);
    card.addEventListener('submit', async e => {
      e.preventDefault(); if (pending) return;
      pending = true; submit.disabled = cancel.disabled = true; error.textContent = '';
      try { await opts.onSave(Object.fromEntries([...inputs].map(([key, el]) => [key, el.value.trim()]))); overlay.remove(); resolve(true); }
      catch (err) { if (overlay.isConnected) error.textContent = (err as Error).message; }
      finally { pending = false; submit.disabled = cancel.disabled = false; }
    });
  });
}
